import { useResetOnClose } from "@/lib/hooks/useResetOnClose"
import React, { FC, Dispatch, SetStateAction, useState } from "react"
import { PencilSquareIcon, TrashIcon } from "@heroicons/react/24/outline"
import { Table } from "@/components"
import { PortOfEntryType, TransloadPartnerType } from "@/lib/types"
import { Modal } from "@/components/general/Modal"
import { useForm } from "react-hook-form"
import { VehicleCard } from "../user/VehicleCard"
import Swal from "sweetalert2"
import { convertEquipmentType, dollarUS } from "@/lib/services"

type Props = {
  popup: boolean
  setPopup: Dispatch<SetStateAction<boolean>>
  listingData: { data: TransloadPartnerType[] }
  portOfEntryListing: { data: PortOfEntryType[] }
  header: {
    name: string
    width: string
  }[]
  handleForm: (form: TransloadPartnerType) => void
  handleFormUpdate: (form: TransloadPartnerType) => void
  handleFormDelete: (form: string) => void
  title: string
  setTitle: (title: string) => void
}

export const TransloadPartner: FC<Props> = (props) => {
  const [portOfEntryId, setPortOfEntryId] = useState("")
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TransloadPartnerType>()

  const onSubmit = (data: TransloadPartnerType) => {
    data.portOfEntryId = portOfEntryId
    data.transloadCost = parseInt(String(data.transloadCost))
    if (data.id == undefined) {
      props.handleForm(data)
    } else {
      props.handleFormUpdate(data)
    }
  }

  const onDelete = (id: string) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      // color: "#ffffff",
      // background: "#333333",
      confirmButtonColor: "#CEA16B",
      cancelButtonColor: "#a33333",
      customClass: {
        popup: 'custom-popup',
      },
      confirmButtonText: "<p style='color: black;'>Yes, Delete it!</p>",
    }).then(async (result) => {
      if (result.isConfirmed) {
        props.handleFormDelete(id)
        Swal.fire({
          icon: "success",
          title: "Record Deleted",
          // background: "#333333",
          toast: true,
          position: "top",
          timer: 2000,
          iconColor: "#CEA16B",
          showCancelButton: false,
          showConfirmButton: false,
          customClass: {
            popup: "custom-success-popup",
          }
        })
      }
    })
  }

  const populateData = (e: TransloadPartnerType) => {
    props.setTitle('Update Transload Partner')
    setValue("id", e.id)
    setValue("equipmentType", e.equipmentType)
    setValue(
      "transloadCarrier",
      e.transloadCarrier == null ? "-" : e.transloadCarrier,
    )
    setValue("transloadCost", e.transloadCost)
    setPortOfEntryId(e.portOfEntryId)
    setValue("portOfEntryId", e.portOfEntryId)
    props.setPopup(true)
  }

  useResetOnClose(props.popup, () => {
    reset({
      equipmentType: "",
      transloadCarrier: "",
      portOfEntryId: "",
      transloadCost: undefined,
      id: undefined,
    })
    setPortOfEntryId("")
  })

  return (
    <>
      <Modal
        open={props.popup}
        close={() => props.setPopup(false)}
        width="small"
        title={props.title}
      >
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card"
                value="DRY_VAN"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card">
                <VehicleCard image="/images/dry-van.svg" name="Dry Van" />
              </label>
            </div>
            <div className="col-span-1">
              <input
                className="hidden vehicle_radio_customize"
                type="radio"
                id="vehicle_card1"
                value="FLATBED"
                {...register("equipmentType", { required: true })}
              />
              <label htmlFor="vehicle_card1">
                <VehicleCard image="/images/flatbed.svg" name="Flatbed" />
              </label>
            </div>
          </div>
          <div className="flex flex-col gap-3 mt-3">
            <div className="grid grid-cols-2 gap-2">
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">
                  Transload Carrier
                </label>
                <input
                  className={
                    (errors.transloadCarrier
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="text"
                  placeholder="Enter Transload Carrier"
                  {...register("transloadCarrier", { required: true })}
                />
              </fieldset>
              <fieldset>
                <label className="text-xs dark:text-primary-8 text-light-1">Transload Cost</label>
                <input
                  className={
                    (errors.transloadCost
                      ? "border-red-400"
                      : "dark:border-secondary-2") +
                    " w-full text-sm p-2 dark:bg-transparent bg-white text-light-2 mt-1 outline-none dark:text-white border-2 rounded placeholder:text-zinc-500 placeholder:text-xs"
                  }
                  type="number"
                  placeholder="Enter Transload Cost"
                  {...register("transloadCost", { required: true })}
                />
              </fieldset>
            </div>

            <fieldset>
              <label className="text-xs dark:text-primary-8 text-light-1">Port Of Entry</label>
              <select
                className={
                  (errors.portOfEntryId
                    ? "border-red-400"
                    : "dark:border-secondary-2") +
                  " w-full text-sm p-2 dark:bg-primary-1 bg-white text-light-2 outline-none dark:text-primary-10 border-2 rounded placeholder:text-xs "
                }
                placeholder="Enter Port Of Entry"
                value={portOfEntryId}
                onChange={(e) => setPortOfEntryId(e.target.value)}
              >
                <option value="" hidden>
                  Select Port Of Entry
                </option>
                {props.portOfEntryListing.data.map((e, i) => (
                  <option value={e.id} key={i}>
                    {e.name}
                  </option>
                ))}
              </select>
            </fieldset>
          </div>
          <button
            className="bg-primary-8 px-4 py-2 rounded font-medium w-full mt-2 dark:text-secondary-1 text-primary-1"
            onClick={handleSubmit(onSubmit)}
            type="button"
          >
            Submit
          </button>
        </div>
      </Modal>
      <div className="m-4">
        <Table
          header={props.header}
          headerAlign="text-left"
          lastheadingAlign="text-right"
        >
          {props.listingData.data.map((e, i) => (
            <tr key={i} className="text-left">
              <td className={"whitespace-nowrap p-4 w-52"}>
                {convertEquipmentType(e.equipmentType) == "Van"
                  ? "Dry Van"
                  : convertEquipmentType(e.equipmentType)}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {e.transloadCarrier == null ? "-" : e.transloadCarrier}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                {dollarUS.format(e.transloadCost)}
              </td>
              <td className={"whitespace-nowrap p-4 w-52"}>
                <p>{e.portOfEntry !== undefined && e.portOfEntry.name}</p>
              </td>
              <td className={"whitespace-nowrap p-4 w-24"}>
                <div className="flex justify-end gap-2">
                  <PencilSquareIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => populateData(e)}
                  />
                  <TrashIcon
                    className="w-4 h-4 text-accent cursor-pointer"
                    onClick={() => onDelete(e.id)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </div>
    </>
  )
}
